import { AppContext } from 'contexts/app-context';
import React, { FocusEvent, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { JobsList } from './jobs-list';
import { Skeleton } from './ui/skeleton';

export function PaginationSkeleton() {
  return (
    <div className="mt-3 flex items-center justify-center">
      <Skeleton className="h-6 w-[200px]" />
    </div>
  );
}

export interface PaginationProps {
  currentPage: number;
  lastPage: number;
  setPage: (value: number) => void;
  jobsCount: number;
}

export function Pagination({
  currentPage,
  lastPage,
  setPage,
  jobsCount,
}: PaginationProps) {
  const {
    isMobile,
    isFetchingJobs,
    updateUrlWithParams,
    accountSlug,
    brandId,
  } = useContext(AppContext);

  const disableNextPage = useMemo(
    () => currentPage === lastPage || !currentPage || lastPage === 0,
    [currentPage, lastPage],
  );

  const setDefaultPage = (e: FocusEvent<HTMLInputElement>) => {
    const newPage = Number(e.target.value);

    if (newPage > 0 && newPage <= lastPage) {
      setPage(newPage);
      if (accountSlug) {
        updateUrlWithParams(accountSlug, brandId, 'page', newPage);
      }
    } else {
      setPage(1);
      if (accountSlug) {
        updateUrlWithParams(accountSlug, brandId, 'page', 1);
      }
      // TODO: Show error toast
    }
  };

  if (isMobile && isFetchingJobs && currentPage > 1) {
    return (
      <>
        <JobsList.Skeleton />
        <PaginationSkeleton />
      </>
    );
  }

  const handlePreviousPage = () => {
    const newPage = currentPage - 1;
    setPage(newPage);
    if (accountSlug) {
      updateUrlWithParams(accountSlug, brandId, 'page', newPage);
    }
  };

  const handleNextPage = () => {
    const newPage = currentPage + 1;
    setPage(newPage);
    if (accountSlug) {
      updateUrlWithParams(accountSlug, brandId, 'page', newPage);
    }
  };

  return (
    <div
      className={`text-14 font-400 mt-3 flex items-center justify-center gap-2 leading-4 ${
        disableNextPage && isMobile ? 'hidden' : ''
      }`}
    >
      {isMobile ? (
        <button
          type="button"
          onClick={handleNextPage}
          className="font-[450] text-blue-400 underline hover:text-blue-600"
        >
          {`See more (${jobsCount - currentPage * 10})`}
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || !currentPage}
            className="bg-white disabled:text-gray-400"
          >
            <FormattedMessage
              defaultMessage="Previous"
              description="Previous page button label"
            />
          </button>
          <p>
            <FormattedMessage defaultMessage="Page" description="Page label" />
          </p>
          <input
            onKeyDown={e =>
              e.keyCode === 13 && (e.target as HTMLElement).blur()
            }
            defaultValue={currentPage}
            onBlur={setDefaultPage}
            className="h-6 w-9 rounded border border-inherit text-center"
          />
          <p>
            <FormattedMessage
              defaultMessage="of {lastPage}"
              description="Number of pages remaining"
              values={{ lastPage }}
            />
          </p>
          <button
            type="button"
            onClick={handleNextPage}
            disabled={disableNextPage}
            className="bg-white disabled:text-gray-400"
          >
            <FormattedMessage
              defaultMessage="Next"
              description="Next page button label"
            />
          </button>
        </>
      )}
    </div>
  );
}
